import React, { useState, useEffect } from "react";
import {
  Mail,
  Calendar,
  Building2,
  MessageSquare,
  Trash2,
  ExternalLink,
  Search,
} from "lucide-react";
import { GetAxios, DeleteAxios } from "../../helpers/axiosHelpers";
import { toast } from "sonner";

const InquiriesPage = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const res = await GetAxios("/inquiry"); // Adjust endpoint as per your API
      if (res.success) setInquiries(res.data);
    } catch (err) {
      toast.error("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    try {
      const res = await DeleteAxios(`/inquiry/${id}`);
      if (res.success) {
        setInquiries(inquiries.filter((i) => i.id !== id));
        toast.success("Inquiry removed");
      }
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const filteredInquiries = inquiries.filter(
    (i) =>
      i.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      i.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      i.subject.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">
            Customer Inquiries
          </h1>
          <p className="text-slate-500 font-bold uppercase text-[10px] tracking-[0.2em] mt-1">
            Direct messages from the contact form
          </p>
        </div>

        <div className="relative group">
          <Search
            className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-subidha-blue transition-colors"
            size={18}
          />
          <input
            type="text"
            placeholder="Search by name, email or subject..."
            className="pl-12 pr-6 py-3.5 bg-white border border-slate-200 rounded-2xl w-full md:w-80 font-bold text-sm outline-none focus:ring-4 focus:ring-subidha-blue/10 focus:border-subidha-blue transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* List Section */}
      <div className="grid grid-cols-1 gap-4">
        {loading ? (
          <div className="py-20 text-center opacity-20 animate-pulse font-black uppercase tracking-widest">
            Loading Inquiries...
          </div>
        ) : filteredInquiries.length > 0 ? (
          filteredInquiries.map((inquiry) => (
            <div
              key={inquiry.id}
              className="bg-white border border-slate-100 rounded-[2rem] p-6 md:p-8 hover:shadow-xl hover:shadow-slate-200/50 transition-all group border-l-4 border-l-transparent hover:border-l-subidha-blue"
            >
              <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                {/* User Info */}
                <div className="flex items-center space-x-4 min-w-[250px]">
                  <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-400 group-hover:bg-subidha-blue group-hover:text-white transition-all duration-300">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h3 className="font-black text-slate-900 leading-none mb-1">
                      {inquiry.name}
                    </h3>
                    <p className="text-xs font-bold text-slate-400 truncate">
                      {inquiry.email}
                    </p>
                  </div>
                </div>

                {/* Metadata */}
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="flex items-center text-xs font-bold text-slate-500 uppercase tracking-widest">
                    <Building2 size={14} className="mr-2 text-slate-300" />
                    {inquiry.company || "No Company"}
                  </div>
                  <div className="flex items-center text-xs font-bold text-slate-500 uppercase tracking-widest">
                    <Calendar size={14} className="mr-2 text-slate-300" />
                    {new Date(inquiry.createdAt).toLocaleDateString()}
                  </div>
                  <div className="lg:col-span-1">
                    <span className="text-[10px] font-black bg-blue-50 text-subidha-blue px-2 py-1 rounded-md uppercase tracking-tighter">
                      Subject: {inquiry.subject}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2 pt-4 lg:pt-0 border-t lg:border-t-0 border-slate-50">
                  <button
                    onClick={() => setSelectedInquiry(inquiry)}
                    className="flex-1 lg:flex-none flex items-center justify-center space-x-2 px-6 py-3 bg-slate-900 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-subidha-blue transition-all"
                  >
                    <MessageSquare size={14} />
                    <span>View Message</span>
                  </button>
                  <button
                    onClick={() => handleDelete(inquiry.id)}
                    className="p-3 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-20 bg-slate-50 rounded-[3rem] border-2 border-dashed border-slate-200">
            <p className="text-slate-400 font-black uppercase tracking-widest text-sm">
              No inquiries matched your search
            </p>
          </div>
        )}
      </div>

      {/* Message Viewer Modal */}
      {selectedInquiry && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[100] flex items-center justify-center p-6">
          <div className="bg-white rounded-[2.5rem] w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 flex flex-col">
            <div className="p-8 border-b border-slate-50 flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-subidha-blue/10 text-subidha-blue rounded-2xl flex items-center justify-center">
                  <MessageSquare size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-black text-slate-900">
                    {selectedInquiry.subject}
                  </h2>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    Message Detail
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedInquiry(null)}
                className="p-2 hover:bg-slate-100 rounded-xl transition-all"
              >
                <X size={24} className="text-slate-400" />
              </button>
            </div>

            <div className="p-8 space-y-6 overflow-y-auto">
              <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 italic text-slate-600 leading-relaxed font-medium whitespace-pre-wrap break-words">
                "{selectedInquiry.message}"
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-white border border-slate-100 rounded-2xl">
                  <p className="text-[9px] font-black text-slate-400 uppercase mb-1">
                    From
                  </p>
                  <p className="text-sm font-bold text-slate-900">
                    {selectedInquiry.name}
                  </p>
                </div>
                <div className="p-4 bg-white border border-slate-100 rounded-2xl">
                  <p className="text-[9px] font-black text-slate-400 uppercase mb-1">
                    Company
                  </p>
                  <p className="text-sm font-bold text-slate-900">
                    {selectedInquiry.company || "N/A"}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-8 bg-slate-50 flex gap-3">
              <button
                onClick={() =>
                  (window.location.href = `mailto:${selectedInquiry.email}`)
                }
                className="flex-1 py-4 bg-subidha-blue text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center justify-center space-x-2"
              >
                <Mail size={16} />
                <span>Reply via Email</span>
              </button>
              <button
                onClick={() => setSelectedInquiry(null)}
                className="px-8 py-4 bg-white border border-slate-200 text-slate-500 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-100 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper X component since Lucide might not be destructuring it
const X = ({ size, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

export default InquiriesPage;
